import { Alert } from '@mui/material';

import { Loader } from '@/components/feedback/Loader';
import { cn } from '@/shared/lib/cn';

import type { SavedSignature } from './SignaturePicker';

interface SavedSignatureListProps {
  signatures: readonly SavedSignature[];
  selectedId: string;
  onSelect: (id: string) => void;
  isLoading: boolean;
}

/**
 * The saved-signature choices, as radios.
 *
 * A real radio group rather than clickable cards: a signature is a single
 * choice from a set, and the native control brings arrow-key navigation and
 * the correct announcement for free.
 */
export const SavedSignatureList = ({
  signatures,
  selectedId,
  onSelect,
  isLoading,
}: SavedSignatureListProps) => {
  if (isLoading) return <Loader size="section" label="Loading your signatures" />;

  if (signatures.length === 0) {
    return (
      <Alert severity="info" className="rounded-lg">
        You have no saved signatures yet. Draw or type one below — you can save it for next time
        from Settings.
      </Alert>
    );
  }

  return (
    <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
      <legend className="mb-2 text-[11px] font-semibold tracking-wide uppercase">
        Choose a signature
      </legend>

      {signatures.map((signature, index) => {
        const isSelected = selectedId === signature.id;
        return (
          <label
            key={signature.id}
            className={cn(
              'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border p-3',
              isSelected ? 'border-primary bg-brand-50' : 'border-divider',
            )}
          >
            <input
              type="radio"
              name="signature"
              value={signature.id}
              checked={isSelected}
              onChange={() => {
                onSelect(signature.id);
              }}
              className="size-4 accent-[var(--mui-palette-primary-main)]"
            />
            {/* A signature image has no text to describe it, so the accessible
                name has to be positional. */}
            <span className="sr-only">{`Signature ${index + 1}`}</span>
            <img
              src={signature.base64Image}
              alt=""
              className="h-10 max-w-full object-contain"
            />
          </label>
        );
      })}
    </fieldset>
  );
};
